import { useCallback } from 'react';
import useDeclarations from 'hooks/useDeclarations';
import useFormUtils from 'hooks/useFormUtils';
import { ParsedHouseConsignment, ParsedItem } from 'store/declaration-form-errors/ParsedForm';
import { ics2LocationFromParsedForm } from 'views/declarations/eu/ics2/errors/parsedFormLocation';
import useGoToIcs2ErrorLocation from 'views/declarations/eu/ics2/errors/useGoToIcs2ErrorLocation';
import useGoToItemErrorField from './useGoToItemErrorField';
import { ErrorBucketLevel, ErrorFieldLink } from './types';

interface BucketIndices {
    houseConsignment?: ParsedHouseConsignment;
    item?: ParsedItem;
}

/**
 * Decides how a listed error gets the reader to its field, so the lists that show errors do not
 * have to know. A declaration type is wired up only once something can resolve its field paths to
 * a route; the rest return nothing and their rows stay plain text.
 */
const useErrorFieldLink = (onNavigate?: () => void) => {
    const { declaration } = useDeclarations();
    const { isAis, isCds } = useFormUtils();

    const goToIcs2Location = useGoToIcs2ErrorLocation(onNavigate);
    const goToItemField = useGoToItemErrorField(onNavigate);

    const isIcs2 = Boolean(declaration?.ics2ENSDeclaration);

    return useCallback(
        (level: ErrorBucketLevel, indices?: BucketIndices): ErrorFieldLink | undefined => {
            if (isIcs2) {
                // ICS2 lists its goods items on levels of its own and never fills the flat bucket.
                if (level === 'item') return undefined;

                return (field) =>
                    field.path ? () => goToIcs2Location(ics2LocationFromParsedForm(level, field, indices)) : undefined;
            }

            // AIS (H1, H7, I1) and CDS all parse into a master plus a flat list of items, and all
            // number those items from one, so they address the form the same way.
            if (isAis || isCds) {
                if (level !== 'master' && level !== 'item') return undefined;

                return (field) => (field.path ? () => goToItemField(indices?.item, field.path) : undefined);
            }

            return undefined;
        },
        [isIcs2, isAis, isCds, goToIcs2Location, goToItemField]
    );
};

export default useErrorFieldLink;
